/**
 * "Aprovar no IW" e "Reprovar no IW" no orçamento do Caso (`TP-068`, T4, NR-229).
 *
 * Aprovação financeira é humana (decisão-âncora 3): a pessoa decide, e a plataforma grava no IW só a aprovação
 * interna, com a versão do orçamento que a pessoa viu. Aprovar exige a alçada registrada (`TP-064`); reprovar não.
 * Sai só com a confirmação de uma pessoa (restrição 9).
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { comoErroDaApi } from '../../../shared/api/erros';
import { ConfirmacaoHumana } from '../../../shared/ui/ConfirmacaoHumana';
import { EstadoDeErro } from '../../../shared/ui/EstadoDeErro';
import { chavesDeCasos } from '../api/casos';
import {
  chaveDaDecisaoDoOrcamento,
  decidirOrcamentoNoIw,
  obterSituacaoDaDecisao,
  ROTULOS_DO_STATUS_NO_IW,
  type DecisaoDoOrcamentoApi,
  type RespostaDaDecisaoApi,
} from './apiDoOrcamentoDoIw';

const ROTULOS: Record<DecisaoDoOrcamentoApi, { botao: string; confirmar: string; gravada: string; status: string }> = {
  Approve: { botao: 'Aprovar no IW', confirmar: 'Aprovar', gravada: 'Aprovação gravada no IW.', status: 'Aprovado' },
  Reject: { botao: 'Reprovar no IW', confirmar: 'Reprovar', gravada: 'Reprovação gravada no IW.', status: 'Reprovado' },
};

function mensagemDoDesfecho(decisao: DecisaoDoOrcamentoApi, resposta: RespostaDaDecisaoApi): string {
  switch (resposta.resultado) {
    case 'Recorded':
      return ROTULOS[decisao].gravada;
    case 'Pending':
      return 'O IW não respondeu agora. A decisão ficou na fila e é refeita sozinha, sem gravar duas vezes.';
    case 'Refused': {
      const campos = resposta.camposRecusados.map((c) => `${c.field}: ${c.message}`).join('; ');
      const detalhe = [campos, resposta.motivo].filter((parte) => parte !== null && parte !== '').join(' ');
      // A parada da plataforma (orçamento alterado no IW, por exemplo) não é recusa do IW.
      const quem = resposta.recusadoPelaPlataforma ? 'Nada foi gravado no IW.' : 'O IW recusou a decisão.';
      return detalhe === '' ? quem : `${quem} ${detalhe}`;
    }
    case 'Unanswered':
      return 'O IW não respondeu em nenhuma tentativa, e nada foi confirmado. Tente de novo quando ele voltar.';
    default:
      return 'O IW respondeu de um jeito que esta tela ainda não conhece. Recarregue o Caso para ver o estado atual.';
  }
}

export function DecisaoDoOrcamentoNoIw({
  casoId,
  competencia,
  orcamento,
}: {
  casoId: string;
  competencia: string;
  orcamento: number;
}): React.JSX.Element {
  const clienteDeConsultas = useQueryClient();
  const [confirmando, definirConfirmando] = useState<DecisaoDoOrcamentoApi | null>(null);
  const chave = chaveDaDecisaoDoOrcamento(casoId, competencia, orcamento);

  const situacao = useQuery({
    queryKey: chave,
    queryFn: ({ signal }) => obterSituacaoDaDecisao(casoId, competencia, orcamento, signal),
    retry: false,
  });

  const decidir = useMutation({
    // A versão é a da tela: se o orçamento mudou no IW desde a leitura, a API recusa e nada é gravado (TP-068 D3).
    mutationFn: ({ decisao, versao }: { decisao: DecisaoDoOrcamentoApi; versao: string }) =>
      decidirOrcamentoNoIw(casoId, competencia, orcamento, versao, decisao),
    onSettled: () => {
      void clienteDeConsultas.invalidateQueries({ queryKey: chave });
      void clienteDeConsultas.invalidateQueries({ queryKey: chavesDeCasos.enviosAoIw(casoId) });
    },
  });

  if (situacao.isError) {
    return <EstadoDeErro erro={situacao.error} contexto="Não foi possível ler a aprovação do orçamento no IW" />;
  }

  const dado = situacao.data;
  if (dado === undefined) return <></>;

  if (!dado.ligada) {
    return (
      <Alert severity="info">
        Aprovar ou reprovar o orçamento pela plataforma ainda não está ligado neste ambiente. Decida direto no IW.
      </Alert>
    );
  }

  if (dado.cancelado) {
    return <Alert severity="info">O orçamento {dado.orcamento} está cancelado no IW: não há aprovação a decidir.</Alert>;
  }

  const statusAtual = dado.statusInterno !== null ? ROTULOS_DO_STATUS_NO_IW[dado.statusInterno] : 'sem status';
  const desligado = (decisao: DecisaoDoOrcamentoApi): boolean =>
    decidir.isPending
    || !dado.podeDecidir
    || dado.versao === null
    || (decisao === 'Approve' && (!dado.alcadaRegistrada || dado.statusInterno === 'Approved'))
    || (decisao === 'Reject' && dado.statusInterno === 'Rejected');

  return (
    <Stack spacing={1} aria-label="Aprovação do orçamento no IW" component="section">
      <Typography variant="body2">Aprovação interna no IW: {statusAtual}</Typography>

      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
        {(['Approve', 'Reject'] as const).map((decisao) => (
          <Button
            key={decisao}
            variant="outlined"
            disabled={desligado(decisao)}
            onClick={() => {
              decidir.reset();
              definirConfirmando(decisao);
            }}
          >
            {ROTULOS[decisao].botao}
          </Button>
        ))}
      </Stack>

      {!dado.podeDecidir ? (
        <Typography variant="caption" color="text.secondary">
          Seu perfil não aprova nem reprova orçamento no IW: a decisão é de quem conduz a captação do caso.
        </Typography>
      ) : !dado.alcadaRegistrada ? (
        <Typography variant="caption" color="text.secondary">
          Registre a alçada de quem aprovou a margem para aprovar no IW. Reprovar não exige alçada.
        </Typography>
      ) : null}

      {decidir.isSuccess ? (
        <Typography role="status" variant="body2">
          {mensagemDoDesfecho(decidir.variables.decisao, decidir.data)}
        </Typography>
      ) : null}

      {decidir.isError ? (
        <Alert severity="error" role="alert">
          {comoErroDaApi(decidir.error).message}
        </Alert>
      ) : null}

      <ConfirmacaoHumana
        aberto={confirmando !== null}
        titulo={confirmando === 'Reject' ? 'Reprovar o orçamento no IW?' : 'Aprovar o orçamento no IW?'}
        descricao={`A plataforma grava no IW a aprovação interna do orçamento ${String(orcamento)} como "${
          confirmando !== null ? ROTULOS[confirmando].status : ''
        }" (hoje: ${statusAtual}). Se o orçamento mudar no IW antes de gravar, nada é gravado e você revisa de novo.`}
        rotuloDeConfirmar={confirmando !== null ? ROTULOS[confirmando].confirmar : 'Confirmar'}
        aoCancelar={() => {
          definirConfirmando(null);
        }}
        aoConfirmar={() => {
          if (confirmando === null || dado.versao === null) return;
          const decisao = confirmando;
          definirConfirmando(null);
          decidir.mutate({ decisao, versao: dado.versao });
        }}
      />
    </Stack>
  );
}
